import { Box, Typography } from "@mui/material";

import { Cartao, listrado, num, plural } from "./comum";
import { FAIXAS, Gente, LinhaPiramide } from "./types";

const COR_TITULARES = "#3024E1";
const COR_DEPENDENTES = "#B4500A";
const ZERO: LinhaPiramide = { titulares: 0, dependentes: 0, titularesQueSobem: 0, dependentesQueSobem: 0 };

/** Um lado da faixa: a parte que fica é cheia e encosta no centro; a que sobe vem listrada por fora. */
function Lado({ faixa, papel, total, sobe, max, cor, esquerda }: {
  faixa: string; papel: "titulares" | "dependentes"; total: number; sobe: number; max: number; cor: string; esquerda: boolean;
}) {
  const q = Math.min(sobe, total);
  const w = (n: number) => `${max > 0 ? (n / max) * 100 : 0}%`;
  const sobem = q > 0 && (
    <Box data-testid={`piramide-${faixa}-${papel}-sobem`} data-sobem="true" sx={{ width: w(q), height: 14, background: listrado(cor) }} />
  );
  const fica = <Box sx={{ width: w(total - q), height: 14, bgcolor: cor }} />;
  return (
    <Box sx={{ display: "flex", flexDirection: esquerda ? "row" : "row-reverse", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 44, textAlign: esquerda ? "left" : "right" }}>
        {num(total)}
      </Typography>
      <Box sx={{ flex: 1, display: "flex", flexDirection: esquerda ? "row-reverse" : "row" }}>
        {fica}
        {sobem}
      </Box>
    </Box>
  );
}

export function Piramide({ piramide, subtitulo }: { piramide: NonNullable<Gente["piramide"]>; subtitulo: string }) {
  const linhas = [...FAIXAS].reverse().map((f) => ({ f, l: piramide[f] ?? ZERO }));
  const max = Math.max(0, ...linhas.flatMap(({ l }) => [l.titulares, l.dependentes]));
  const sn = piramide.semNascimento;
  const legenda = (txt: string, fundo: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{ width: 14, height: 14, background: fundo, borderRadius: 0.5 }} />
      <Typography variant="body2">{txt}</Typography>
    </Box>
  );
  return (
    <Cartao
      titulo="Pirâmide por faixa etária"
      subtitulo={subtitulo}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {legenda("Titulares", COR_TITULARES)}
        {legenda("Dependentes", COR_DEPENDENTES)}
        {legenda("Muda de faixa nos próximos 12 meses", listrado("#6B6B7B"))}
      </Box>
      <Box
        role="img"
        aria-label="Pirâmide etária: titulares e dependentes por faixa, com a parte que muda de faixa em 12 meses"
        sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
      >
        {linhas.map(({ f, l }) => (
          <Box
            key={f}
            data-testid={`piramide-faixa-${f}`}
            sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 96px minmax(0,1fr)", gap: 1, alignItems: "center" }}
          >
            <Lado faixa={f} papel="titulares" total={l.titulares} sobe={l.titularesQueSobem} max={max} cor={COR_TITULARES} esquerda />
            <Typography variant="body2" textAlign="center">
              {f}
            </Typography>
            <Lado faixa={f} papel="dependentes" total={l.dependentes} sobe={l.dependentesQueSobem} max={max} cor={COR_DEPENDENTES} esquerda={false} />
          </Box>
        ))}
      </Box>
      {sn && sn.titulares + sn.dependentes > 0 && (
        <Typography variant="body2" color="text.secondary" data-testid="piramide-sem-nascimento">
          {`Sem data de nascimento no cadastro: ${plural(sn.titulares, "titular", "titulares")} e ${plural(sn.dependentes, "dependente", "dependentes")}.`}
        </Typography>
      )}
      <Box
        data-testid="piramide-custo-depois"
        sx={{ border: "1px dashed", borderColor: "grey.400", borderRadius: 1, p: 1.5, display: "flex", gap: 1, alignItems: "center" }}
      >
        <Typography variant="caption" sx={{ px: 1, borderRadius: 1, bgcolor: "grey.100", fontWeight: 600 }}>
          depois
        </Typography>
        <Typography variant="body2" color="text.secondary">
          O custo por faixa entra numa próxima etapa.
        </Typography>
      </Box>
    </Cartao>
  );
}
